import React, { useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { useNavigate } from 'react-router-dom';

// Game stats data matching the image
const columns = [
  { header: 'Game', key: 'game' },
  { header: 'Date', key: 'date' },
  { header: 'Type', key: 'type' },
  { header: 'Outcome', key: 'outcome' },
  { header: 'Links', key: 'links' },
];

// Update GameStatsTable to accept games as a prop
function GameStatsTable({ games }: { games: any[] }) {
  const navigate = useNavigate();

  const handleStatsClick = (gameId: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    sessionManager.setGameId(gameId);
    navigate(`/games/${gameId}/stats`);
  };

  return (
    <div className="overflow-x-auto shadow-xl">
      <table className="text-center border border-[#22c55e] table-fixed w-full">
        <colgroup>
          <col style={{ width: '260px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '180px' }} />
        </colgroup>
        <thead>
          <tr className="bg-[#05396b] text-white">
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-2 border border-[#22c55e] text-xl font-semibold">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-base bg-[#f6fff0]">
          {games.map((row, idx) => (
            <tr key={row._id || row.game + idx} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
              <td className="px-3 py-2 border border-gray-400 text-left">{row.opponent?.name}</td>
              <td className="px-3 py-2 border border-gray-400">{row.date}</td>
              <td className="px-3 py-2 border border-gray-400">{row.type}</td>
              <td className="px-3 py-2 border border-gray-400">{row.outcome}</td>
              <td className="px-3 py-2 border border-gray-400">
                <a href={`/games/${row._id}/teams`} className="text-green-600 font-semibold hover:underline mr-2">
                  Teams
                </a>
                <a href={`/games/${row._id}/players`} className="text-green-600 font-semibold hover:underline mr-2">
                  Players
                </a>
                <a
                  href={`/games/${row._id}/stats`}
                  className="text-green-600 font-semibold hover:underline"
                  onClick={(e) => handleStatsClick(row._id, e)}
                >
                  Stats
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EditStatsPage: React.FC = () => {
  const [games, setGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // TODO: Replace with actual clientId and seasonId
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`${API_URLS.GET_SEASON_GAMES}`, {
          params: { clientId, seasonId },
        });

        if (response.data.success) {
          setGames(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch games');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch games');
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, [clientId, seasonId]);

  return (
    <div className="w-full relative p-4">
      <h1 className="text-2xl md:text-3xl font-bold text-[#19133c] mb-8">Season Games</h1>
      <div className="relative flex flex-col items-center w-full pb-4 border border-[#bccedf] overflow-auto">
        <div className="flex flex-col z-10 pt-8 w-full px-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : error ? (
            <div className="text-center text-red-600 py-8">{error}</div>
          ) : (
            <GameStatsTable games={games} />
          )}
          <div className="flex justify-center w-full py-4 z-10">
            <img src="/icons/icon-down.png" alt="down" className="w-12 h-12" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStatsPage;
